<template>
  <div class="freight-allowance-view-edit">
    <PopupMoneyInput
      id="freight-allowance-view-edit-popup"
      v-model="freightAllowanceAmount"
      :label="t('shipments.freightAllowance.freightAllowance')"
      :disabled="disabled || !hasUpdateFreightAllowancePermission"
      nullable
      :min="0"
    >
      <template #placeholder="{ label }">
        <KeyValuePair
          :class="{ 'no-label': !label }"
          :label="label"
          :value="freightAllowanceAmountRenderedMoney"
          uppercase-label
        />
      </template>
    </PopupMoneyInput>

    <RygenButton
      v-if="displaySuggestedAmountButton"
      class="suggestion-button text-left text-sm"
      mode="link"
      :label="
        t('common.useSuggestedParam', {
          param: freightAllowanceSuggestedAmountRenderedMoney,
        })
      "
      @click="onUseSuggestedFreightAllowanceClicked"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import authorization from '@rygen/rygen-ui/authorization';
import { renderMoney } from '@rygen/rygen-ui/i18n';

import { FreightAllowance } from '@/model';
import MoneyUtil from '@/util/MoneyUtil';

import PopupMoneyInput from '@/components/form/PopupMoneyInput.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import KeyValuePair from '@rygen/rygen-ui/components/ui/KeyValuePair.vue';

const { t } = useI18n();

const props = withDefaults(
  defineProps<{
    modelValue: FreightAllowance | undefined;
    disabled: boolean;
  }>(),
  {
    modelValue: undefined,
    disabled: false,
  },
);
const { modelValue, disabled } = toRefs(props);
const emit = defineEmits<{
  (event: 'update:model-value', newVal?: FreightAllowance): void;
}>();

const hasUpdateFreightAllowancePermission = computed(() =>
  authorization.hasAuthority('shipment', 'updateFreightAllowance'),
);

const freightAllowanceAmount = computed({
  get: () => MoneyUtil.clone(modelValue.value?.amount),
  set: (newVal) => {
    if (!MoneyUtil.equals(newVal, modelValue.value?.amount)) {
      const updatedFreightAllowance = new FreightAllowance({
        ...modelValue.value,
        amount: MoneyUtil.clone(newVal),
      });
      emit('update:model-value', updatedFreightAllowance);
    }
  },
});

const freightAllowanceAmountRenderedMoney = computed(() =>
  renderMoney(freightAllowanceAmount.value, {
    currencyCode: freightAllowanceAmount?.value?.currency,
  }),
);

const freightAllowanceSuggestedAmount = computed(
  () => modelValue.value?.autoCalc?.suggestedAmount,
);

const freightAllowanceSuggestedAmountRenderedMoney = computed(() => {
  if (!freightAllowanceSuggestedAmount.value) {
    return;
  }
  return renderMoney(freightAllowanceSuggestedAmount.value, {
    currencyCode: freightAllowanceSuggestedAmount?.value?.currency,
  });
});

const displaySuggestedAmountButton = computed(
  () =>
    freightAllowanceSuggestedAmount.value &&
    !MoneyUtil.equals(
      freightAllowanceSuggestedAmount.value,
      freightAllowanceAmount.value,
    ),
);

function onUseSuggestedFreightAllowanceClicked() {
  if (!freightAllowanceSuggestedAmount.value) {
    return;
  }

  freightAllowanceAmount.value = MoneyUtil.clone(
    freightAllowanceSuggestedAmount.value,
  );
}
</script>

<style>
.freight-allowance-view-edit {
  background-color: var(--p-gray-100);
  border: 1px solid var(--p-gray-200);
  border-radius: var(--p-border-radius-sm);
  padding: var(--sp-3);

  .popup-inputgroup {
    .popup-inputgroup-edit-icon {
      right: calc(var(--sp-3) * -1);
    }

    .popup-inputgroup-card {
      width: max-content;
    }
  }

  .suggestion-button {
    margin-top: var(--sp-2);
  }
}
</style>
